import { useState } from "react";
import { BsEye, BsEyeSlash, EyeIcon, UploadIcon } from "../../icons/CommonIcons";
import Button from "./Button";
import { BASE_URL } from "../../config/apiConfig";

const Input = ({
  id,
  type,
  name,
  placeholder,
  required = false,
  value,
  onChange,
  onBlur,
  className = "",
  checked,
  maxLength = 255,
  disabled = false,
  options = [],
  min,
  max,
  label = "",
  language = false,
}) => {
  const commonProps = {
    id,
    name,
    required,
    onChange,
    onBlur,
    disabled,
  };

  // ✅ Select dropdown
  if (type === "select") {
    return (
      <select
        {...commonProps}
        value={value || ""}
        className={`border rounded px-3 py-2 text-sm ${className}`}
      >
        <option value="" disabled hidden>
          {placeholder || (language ? "कोई विकल्प चुनें" : "Select an option")}
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {language ? opt.hi : opt.label}
          </option>
        ))}
      </select>
    );
  }

  // ✅ File upload
  if (type === "file") {
    const fileInputId = `file-${id || name}`;
    const displayInputId = `file-name-${id || name}`;
    const [fileObj, setFileObj] = useState(null);

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      setFileObj(file);
      onChange(e); // 🔹 still call parent change handler

      // update text field with selected file name
      const fileNameInput = document.getElementById(displayInputId);
      if (fileNameInput) {
        fileNameInput.value = file?.name || "";
      }
    };

    const handleViewFile = () => {
      if (fileObj) {
        // ✅ Preview newly selected file
        const url = URL.createObjectURL(fileObj);
        window.open(url, "_blank");
      } else if (value?.url) {
        // ✅ Open existing uploaded file
        const fileUrl = value.url.startsWith("http")
          ? value.url
          : `${BASE_URL}/${value.url}`;
        window.open(fileUrl, "_blank");
      } else {
        alert("No file available to view");
      }
    };

    return (
      <div className="flex gap-2">
        {/* hidden file input */}
        <input
          {...commonProps}
          type="file"
          className="hidden"
          id={fileInputId}
          accept=".pdf"
          onChange={handleFileChange}
        />

        {/* display filename */}
        <input
          type="text"
          id={displayInputId}
          placeholder={
            placeholder || (language ? "फ़ाइल अपलोड करें" : "Upload file")
          }
          className="flex-grow border rounded-l px-3 py-2 text-sm bg-white"
          readOnly
          defaultValue={fileObj?.name || value?.name || ""}
        />

        {/* upload button */}
        <label
          htmlFor={fileInputId}
          className={`px-3 py-2 text-sm rounded flex items-center justify-center
    ${
      disabled
        ? "bg-gray-300 text-gray-500 cursor-not-allowed pointer-events-none"
        : "bg-btn text-white cursor-pointer"
    }`}
        >
          <UploadIcon fill={disabled ? "#999" : "white"} />
        </label>

        {/* view button */}
        <Button
          type="button"
          onClick={handleViewFile}
          className="px-3 py-2 bg-btn text-white text-sm rounded"
          disabled={!fileObj && !value?.url}
        >
          <EyeIcon />
        </Button>
      </div>
    );
  }

  if (type === "checkbox") {
    return (
      <label className={`flex items-center gap-2 cursor-pointer ${className}`}>
        <input
          {...commonProps}
          type="checkbox"
          checked={checked ?? !!value} // 👈 respect checked prop first
          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <span className="text-sm text-gray-700">{label || placeholder}</span>
      </label>
    );
  }

  if (type === "password") {
    const [showPassword, setShowPassword] = useState(false);

    return (
      <span className="relative w-full">
        <input
          {...commonProps}
          type={showPassword ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          maxLength={maxLength}
          className={`border rounded px-3 py-2 text-sm w-full pr-10 ${className}`}
        />

        <span
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600"
          onClick={() => setShowPassword(!showPassword)}
        >
          {showPassword ? <BsEyeSlash /> : <BsEye />}
        </span>
      </span>
    );
  }

  // ✅ Default input
  const inputProps = {
    ...commonProps,
    type,
    placeholder,
    maxLength,
    value,
    className: `border rounded px-3 py-2 text-sm ${className}`,
    ...(min ? { min } : {}),
    ...(max ? { max } : {}),
  };

  return <input {...inputProps} />;
};

export default Input;
